/**
 * Emergent Marketplace — Phase 5
 * VendorReviews.jsx
 * Architectural Layer: Presentation → Vendor Dashboard
 *
 * Vendor manages their reviews:
 *   - Rating summary + distribution bar chart
 *   - Filter by star rating or unanswered
 *   - Inline reply to any review
 *   - Product vs session review distinction
 *
 * Design: Editorial warmth — aged ivory, deep walnut, amber star.
 * Font: Vollkorn (serif display) + Karla (body)
 */

import { useState, useEffect } from "react";
import {
  Star, MessageSquare, Check, ChevronDown, ChevronUp,
  Package, Calendar, Filter, AlertCircle
} from "lucide-react";
import { apiFetch } from "./api";
import { Pagination } from "../../components/Pagination";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Vollkorn:ital,wght@0,400;0,600;1,400&family=Karla:wght@300;400;500;600&display=swap');

  .vr-root {
    --walnut:      #2d1f0e;
    --walnut-mid:  #4a3520;
    --amber:       #c09020;
    --amber-light: #e0b840;
    --amber-bg:    #fdf9ea;
    --ivory:       #faf7f0;
    --ivory-dark:  #f0ead8;
    --white:       #ffffff;
    --border:      #e4dcc8;
    --ink:         #1a1410;
    --ink-mid:     #5a5040;
    --ink-light:   #9a9080;
    --teal:        #1a5a44;
    --teal-bg:     #eaf5f0;
    --teal-border: #a0d8c4;
    font-family: 'Karla', sans-serif;
    background: var(--ivory);
    color: var(--ink);
    min-height: 100vh;
  }

  .vr-header {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 20px 28px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .vr-header h1 {
    font-family: 'Vollkorn', serif;
    font-size: 22px;
    font-weight: 600;
    color: var(--walnut);
    margin: 0 0 2px;
  }

  .vr-header p { font-size: 13px; color: var(--ink-light); margin: 0; }

  /* ── Rating overview ─────────────────────────────────────────────────── */
  .vr-overview {
    display: flex;
    gap: 24px;
    padding: 20px 28px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    align-items: center;
  }

  .vr-score {
    text-align: center;
    flex-shrink: 0;
  }

  .vr-score-num {
    font-family: 'Vollkorn', serif;
    font-size: 52px;
    font-weight: 600;
    color: var(--walnut);
    line-height: 1;
    margin-bottom: 6px;
  }

  .vr-stars {
    display: flex;
    gap: 3px;
    justify-content: center;
    margin-bottom: 4px;
  }

  .vr-score-count { font-size: 12px; color: var(--ink-light); }

  .vr-distribution { flex: 1; }

  .vr-dist-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 5px;
    font-size: 12px;
    color: var(--ink-mid);
  }

  .vr-dist-label { width: 40px; text-align: right; flex-shrink: 0; font-weight: 600; }

  .vr-dist-bar-track {
    flex: 1;
    height: 8px;
    background: var(--ivory-dark);
    border-radius: 4px;
    overflow: hidden;
  }

  .vr-dist-bar-fill {
    height: 100%;
    border-radius: 4px;
    background: var(--amber);
    transition: width 0.4s ease;
  }

  .vr-dist-count { width: 28px; flex-shrink: 0; }

  /* ── Toolbar ─────────────────────────────────────────────────────────── */
  .vr-toolbar {
    display: flex;
    gap: 8px;
    padding: 14px 28px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
  }

  .vr-filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid var(--border);
    background: var(--ivory);
    font-family: 'Karla', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--ink-mid);
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
  }

  .vr-filter-btn.active {
    background: var(--walnut);
    border-color: var(--walnut);
    color: white;
  }

  .vr-filter-btn:hover:not(.active) { border-color: var(--walnut-mid); color: var(--walnut); }

  /* ── Review list ─────────────────────────────────────────────────────── */
  .vr-list { padding: 16px 28px 40px; display: flex; flex-direction: column; gap: 12px; }

  .vr-review-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 14px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .vr-review-card:hover { box-shadow: 0 3px 14px rgba(0,0,0,0.06); }

  .vr-review-main { padding: 18px 20px 14px; }

  .vr-review-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 10px;
    gap: 12px;
  }

  .vr-review-author {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .vr-author-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--walnut), var(--walnut-mid));
    color: rgba(255,255,255,0.9);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Vollkorn', serif;
    font-size: 15px; font-weight: 600;
    flex-shrink: 0;
  }

  .vr-author-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 2px;
  }

  .vr-review-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--ink-light);
  }

  .vr-review-source {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .vr-source-product { background: var(--ivory-dark); color: var(--ink-mid); }
  .vr-source-session  { background: var(--teal-bg); color: var(--teal); }

  .vr-review-rating { flex-shrink: 0; }

  .vr-review-title {
    font-family: 'Vollkorn', serif;
    font-size: 16px;
    font-weight: 600;
    color: var(--ink);
    margin: 0 0 6px;
  }

  .vr-review-body {
    font-size: 14px;
    color: var(--ink-mid);
    line-height: 1.65;
    margin: 0 0 12px;
  }

  .vr-product-ref {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--ink-light);
    background: var(--ivory-dark);
    padding: 3px 9px;
    border-radius: 8px;
    border: 1px solid var(--border);
  }

  /* Vendor reply */
  .vr-reply-section {
    border-top: 1px solid var(--ivory-dark);
    background: var(--ivory);
    padding: 14px 20px;
  }

  .vr-existing-reply {
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .vr-reply-icon {
    width: 28px; height: 28px;
    border-radius: 50%;
    background: var(--teal-bg);
    border: 1px solid var(--teal-border);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: var(--teal);
  }

  .vr-reply-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--teal);
    margin-bottom: 4px;
  }

  .vr-reply-text {
    font-size: 13px;
    color: var(--ink-mid);
    line-height: 1.6;
    margin: 0;
  }

  .vr-reply-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: var(--white);
    font-family: 'Karla', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: var(--ink-mid);
    cursor: pointer;
    transition: all 0.15s;
  }

  .vr-reply-btn:hover { border-color: var(--teal); color: var(--teal); }

  .vr-reply-compose {
    width: 100%;
    background: var(--white);
    border: 1.5px solid var(--border);
    border-radius: 9px;
    padding: 10px 12px;
    font-family: 'Karla', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    resize: vertical;
    min-height: 70px;
    box-sizing: border-box;
    margin-bottom: 10px;
    transition: border-color 0.15s;
  }

  .vr-reply-compose:focus { border-color: var(--teal); background: var(--white); }

  .vr-reply-actions { display: flex; gap: 8px; }

  .vr-btn-submit {
    display: flex; align-items: center; gap: 6px;
    padding: 8px 18px;
    border-radius: 8px;
    background: var(--teal);
    border: none;
    color: white;
    font-family: 'Karla', sans-serif;
    font-size: 13px; font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .vr-btn-submit:hover { background: #2a7a5e; }
  .vr-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }

  .vr-btn-cancel {
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid var(--border);
    background: none;
    font-family: 'Karla', sans-serif;
    font-size: 13px; color: var(--ink-mid);
    cursor: pointer;
  }

  /* Empty */
  .vr-empty {
    text-align: center; padding: 60px 24px; color: var(--ink-light);
  }
`;

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_SUMMARY = {
  avg: 4.8,
  count: 43,
  distribution: { 5: 28, 4: 10, 3: 3, 2: 1, 1: 1 },
};

const MOCK_REVIEWS = [
  {
    id: "r1",
    customer_name: "Priya N.",
    rating: 5,
    title: "Profoundly transformative",
    body: "This session shifted something deep in me. Maya's presence was grounding and her questions were exactly what I needed to move through the block I'd been holding for months. I left feeling 10 pounds lighter.",
    product_title: "Clarity Session",
    product_id: null,
    booking_id: "bk3",
    source: "session",
    vendor_reply: null,
    created_at: "2026-03-11T10:22:00Z",
    is_verified: true,
  },
  {
    id: "r2",
    customer_name: "Lucas D.",
    rating: 5,
    title: "Worth every penny",
    body: "The Morning Awakening series has genuinely changed my mornings. I've been using it every day for three weeks. The pacing is perfect and Maya's voice is incredibly calming.",
    product_title: "Morning Awakening Series",
    product_id: "p1",
    booking_id: null,
    source: "product",
    vendor_reply: "Thank you so much Lucas — this message means the world. So glad the series found its way into your mornings. 🌅",
    created_at: "2026-03-08T14:55:00Z",
    is_verified: true,
  },
  {
    id: "r3",
    customer_name: "Sofia R.",
    rating: 4,
    title: "Beautiful crystal, quick shipping",
    body: "The bowl is gorgeous — even more beautiful in person than in the photos. Shipped quickly and arrived well-packaged. Would give 5 stars but took a few days longer than expected.",
    product_title: "Selenite Charging Bowl",
    product_id: "p2",
    booking_id: null,
    source: "product",
    vendor_reply: null,
    created_at: "2026-03-05T09:30:00Z",
    is_verified: true,
  },
  {
    id: "r4",
    customer_name: "Kai T.",
    rating: 5,
    title: "Exactly what I was looking for",
    body: "The 21-day program is structured beautifully. Each day builds on the last and the reflection prompts are deep. I'm on day 14 and already feel a real shift in how I'm living.",
    product_title: "Conscious Living: 21 Days",
    product_id: "p3",
    booking_id: null,
    source: "product",
    vendor_reply: null,
    created_at: "2026-02-28T16:44:00Z",
    is_verified: true,
  },
];

const formatDate = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// ─── StarRow ──────────────────────────────────────────────────────────────────

function StarRow({ rating, size = 14 }) {
  return (
    <div className="vr-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={size}
          color={n <= rating ? "#c09020" : "#e4dcc8"}
          fill={n <= rating ? "#c09020" : "none"}
        />
      ))}
    </div>
  );
}

// ─── Review Card ──────────────────────────────────────────────────────────────

function ReviewCard({ review, onReplySubmit }) {
  const [replying, setReplying]   = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localReply, setLocalReply] = useState(review.vendor_reply);
  const [replyError, setReplyError] = useState(null);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    setReplyError(null);
    try {
      await onReplySubmit?.(review.id, replyText);
      setLocalReply(replyText);
      setReplying(false);
    } catch (e) {
      setReplyError(e?.message || "Your reply could not be saved. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vr-review-card">
      <div className="vr-review-main">
        <div className="vr-review-top">
          <div className="vr-review-author">
            <div className="vr-author-avatar">{(review.customer_name || "Member")[0]}</div>
            <div>
              <p className="vr-author-name">{review.customer_name || "Member"}</p>
              <div className="vr-review-meta">
                <span>{formatDate(review.created_at)}</span>
                <span className={`vr-review-source vr-source-${review.source}`}>
                  {review.source === "session" ? <><Calendar size={9} /> Session</> : <><Package size={9} /> Product</>}
                </span>
                {review.is_verified && (
                  <span style={{ color: "#1a5a44", display: "flex", alignItems: "center", gap: 3 }}>
                    <Check size={10} /> Verified
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="vr-review-rating">
            <StarRow rating={review.rating} size={15} />
          </div>
        </div>

        {review.title && <p className="vr-review-title">{review.title}</p>}
        <p className="vr-review-body">{review.body}</p>

        {review.product_title && (
          <span className="vr-product-ref">
            <Package size={11} /> {review.product_title}
          </span>
        )}
      </div>

      {/* Reply section */}
      <div className="vr-reply-section">
        {localReply ? (
          <div className="vr-existing-reply">
            <div className="vr-reply-icon"><MessageSquare size={13} /></div>
            <div style={{ flex: 1 }}>
              <p className="vr-reply-label">Your reply</p>
              <p className="vr-reply-text">{localReply}</p>
            </div>
          </div>
        ) : replying ? (
          <div>
            <textarea
              className="vr-reply-compose"
              placeholder="Write a thoughtful reply to this review..."
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              autoFocus
              data-testid="vendor-reply-input"
            />
            {replyError && (
              <p style={{ color: "#a12b2b", fontSize: 13, margin: "8px 0 0" }} data-testid="vendor-reply-error">
                {replyError}
              </p>
            )}
            <div className="vr-reply-actions">
              <button className="vr-btn-submit" onClick={submitReply} disabled={submitting || !replyText.trim()} data-testid="vendor-reply-submit">
                {submitting ? "Posting..." : <><Check size={13} /> Post reply</>}
              </button>
              <button className="vr-btn-cancel" onClick={() => { setReplying(false); setReplyText(""); }} data-testid="vendor-reply-cancel">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="vr-reply-btn" onClick={() => setReplying(true)} data-testid={`vendor-reply-btn-${review.id}`}>
            <MessageSquare size={13} /> Reply to this review
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorReviews() {
  const [reviews, setReviews]   = useState([]);
  const [filter, setFilter]     = useState("all");
  const [summary, setSummary]   = useState({ avg: 0, count: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } });
  const [page, setPage]         = useState(1);
  const PER_PAGE                = 25;

  useEffect(() => { setPage(1); }, [filter]);

  useEffect(() => {
    apiFetch("/vendor/reviews?per_page=200").then(resp => {
      const data = resp?.reviews || resp || [];
      setReviews(data);
      if (data.length > 0) {
        const avg = data.reduce((s, r) => s + r.rating, 0) / data.length;
        const dist = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        data.forEach(r => { dist[r.rating] = (dist[r.rating] || 0) + 1; });
        setSummary({ avg: Math.round(avg * 10) / 10, count: data.length, distribution: dist });
      }
    }).catch(() => {});
  }, []);

  const unreplied = reviews.filter(r => !r.vendor_reply).length;

  const filtered = reviews.filter(r => {
    if (filter === "unreplied")  return !r.vendor_reply;
    if (filter === "5") return r.rating === 5;
    if (filter === "4") return r.rating === 4;
    if (filter === "3") return r.rating <= 3;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleReplySubmit = async (reviewId, reply) => {
    try {
      await apiFetch(`/reviews/${reviewId}/reply`, {
        method: "POST", body: JSON.stringify({ vendor_reply: reply }),
      });
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, vendor_reply: reply } : r));
    } catch (e) {
      throw e;
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="vr-root">
        <div className="vr-header">
          <div>
            <h1>Reviews</h1>
            <p>{summary.count} total · {unreplied} awaiting your reply</p>
          </div>
        </div>

        {/* Rating overview */}
        <div className="vr-overview">
          <div className="vr-score">
            <div className="vr-score-num">{summary.avg.toFixed(1)}</div>
            <StarRow rating={Math.round(summary.avg)} size={16} />
            <div className="vr-score-count">{summary.count} reviews</div>
          </div>
          <div className="vr-distribution">
            {[5, 4, 3, 2, 1].map(n => {
              const count = summary.distribution[n] || 0;
              const pct   = summary.count > 0 ? (count / summary.count) * 100 : 0;
              return (
                <div key={n} className="vr-dist-row">
                  <span className="vr-dist-label">{n} ★</span>
                  <div className="vr-dist-bar-track">
                    <div className="vr-dist-bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="vr-dist-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Filters */}
        <div className="vr-toolbar">
          <button className={`vr-filter-btn ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>
            All ({reviews.length})
          </button>
          {unreplied > 0 && (
            <button className={`vr-filter-btn ${filter === "unreplied" ? "active" : ""}`} onClick={() => setFilter("unreplied")}>
              <AlertCircle size={12} /> Unreplied ({unreplied})
            </button>
          )}
          {["5", "4", "3"].map(n => (
            <button
              key={n}
              className={`vr-filter-btn ${filter === n ? "active" : ""}`}
              onClick={() => setFilter(n)}
            >
              {n} ★ ({n === "3" ? "3 & below" : summary.distribution[parseInt(n)] || 0})
            </button>
          ))}
        </div>

        {/* List */}
        <div className="vr-list">
          {filtered.length === 0 ? (
            <div className="vr-empty">
              <Star size={32} style={{ opacity: 0.2, marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
              <p style={{ fontFamily: "'Vollkorn', serif", fontSize: 18, color: "var(--ink)", margin: "0 0 6px" }}>No reviews yet</p>
              <p style={{ fontSize: 13, margin: 0 }}>Reviews will appear here after customers complete purchases or sessions.</p>
            </div>
          ) : (
            pageItems.map(r => (
              <ReviewCard key={r.id} review={r} onReplySubmit={handleReplySubmit} />
            ))
          )}
        </div>
        <Pagination page={page} totalPages={totalPages} onChange={setPage} testidPrefix="vendor-reviews-pagination" />
      </div>
    </>
  );
}
